import { notification } from "antd";
import { useEffect, useState } from "react";

import api from "../../../api";
import { AccountOption, KontaxTransactionFilterOptions } from "../../../types";
import { getServerErrorNotification } from "../../../utils/error";
import CustomerInfoBanner from "../../common/CustomerInfoBanner";
import { getDefaultFilterState } from "../../common/filter";
import { ContentWrapper } from "../../common/styledComponents";
import TransactionFilters, { Filter } from "../../common/TransactionFilters";
import { KontaxUserContext } from "../../contexts/KontaxUserContext";
import useTransactionFilterParams from "../../hooks/useTransactionFilterParams";
import TransactionList from "./TransactionList";

const VerificationView = () => {
  const [filterParams, setFilterParams] = useTransactionFilterParams();
  const [filterOptions, setFilterOptions] =
    useState<KontaxTransactionFilterOptions>({
      ...getDefaultFilterState(),
      escalated: undefined, // We want to see both escalated and normal transactions on this view
      ...filterParams,
      /*
       * The only `categorized` state this view can express is the category
       * dropdown's "Uncategorized" entry, i.e. `false` — the Booking status
       * radios that also offer `true` are in `hiddenFilters` below. A hand-built
       * `?categorized=1` would otherwise seed a filter that nothing on screen
       * shows and nothing can switch off, so accept only `false` from the URL.
       */
      categorized: filterParams.categorized === false ? false : undefined,
      includeAll: true,
    });
  const [currencyOptions, setCurrencyOptions] = useState<string[]>([]);
  const [accountOptions, setAccountOptions] = useState<AccountOption[]>([]);

  // The currency dropdown is scoped to the currently-viewed customer, so we
  // refetch the available currencies whenever the selected email changes.
  useEffect(() => {
    if (!filterOptions.email) {
      setCurrencyOptions([]);
      return;
    }

    let cancelled = false;
    api.kontax
      .fetchTransactionCurrencies({
        email: filterOptions.email,
        includeAll: true,
      })
      .then((currencies) => {
        if (!cancelled) {
          setCurrencyOptions(currencies);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          const { message: title, description } = getServerErrorNotification(
            error,
            "Error while fetching currency options"
          );
          notification.error({
            message: title,
            ...(description ? { description } : {}),
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [filterOptions.email]);

  // The bank/account filter and the bank-logo column both draw on the customer's
  // connected accounts, so we (re)fetch them whenever the selected email changes.
  useEffect(() => {
    if (!filterOptions.email) {
      setAccountOptions([]);
      return;
    }

    let cancelled = false;
    api.kontax
      .fetchTransactionAccounts({
        email: filterOptions.email,
        includeAll: true,
      })
      .then((accounts) => {
        if (!cancelled) {
          setAccountOptions(accounts);
        }
      })
      .catch(() => {
        // The bank column + account filter are an enhancement layer: if the
        // lookup fails (e.g. smarta-banking is unavailable), degrade quietly to
        // no options / no logos rather than blocking the operator with an error.
        if (!cancelled) {
          setAccountOptions([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [filterOptions.email]);

  return (
    <ContentWrapper>
      <TransactionFilters
        onChangeHandler={(
          updatedFilterOptions: Partial<KontaxTransactionFilterOptions>
        ) => {
          const newFilterOptions = {
            ...filterOptions,
            ...updatedFilterOptions,
          };
          setFilterOptions(newFilterOptions);
          setFilterParams(updatedFilterOptions);
        }}
        email={filterOptions.email}
        direction={filterOptions.direction}
        source={filterOptions.source}
        dateFrom={filterOptions.dateFrom}
        dateTo={filterOptions.dateTo}
        categories={filterOptions.categories}
        // Drives the "Uncategorized" quick filter in the category dropdown.
        // The Booking status radios are hidden here, so this view has no other
        // control over `categorized`.
        categorized={filterOptions.categorized}
        vatCategoryCodes={filterOptions.vatCategoryCodes}
        search={filterOptions.search}
        excludeInternal={filterOptions.excludeInternal}
        accountIds={filterOptions.accountIds}
        accountOptions={accountOptions}
        hiddenFilters={[
          Filter.STATUS,
          Filter.ASSETS,
          Filter.VAT_PAYMENT_FREQUENCY,
          Filter.PLAN,
          Filter.CATEGORIZED,
          Filter.ASSIGNEE,
        ]}
      />
      <KontaxUserContext email={filterOptions.email}>
        <CustomerInfoBanner />
        <TransactionList
          filterOptions={filterOptions}
          currencyOptions={currencyOptions}
          accountOptions={accountOptions}
        />
      </KontaxUserContext>
    </ContentWrapper>
  );
};

export default VerificationView;
